import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaSync, FaWallet, FaPercentage, FaExchangeAlt, FaCheckCircle, FaExclamationTriangle, FaChartLine } from 'react-icons/fa';
import AdminAPI from '../../utils/adminApi';
import { formatCurrency } from '../../utils/formatCurrency';

const AdminReconciliation = () => {
  const [reconciliation, setReconciliation] = useState(null);
  const [stripeBalance, setStripeBalance] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'creators', 'audit'

  useEffect(() => {
    fetchReconciliation();
    fetchStripeBalance();
  }, []);

  const fetchReconciliation = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await AdminAPI.get('/api/manage/reconciliation');
      setReconciliation(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch reconciliation data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeBalance = async () => {
    try {
      setLoadingBalance(true);
      const data = await AdminAPI.get('/api/manage/stripe/balance');
      setStripeBalance(data);
    } catch (err) {
      console.error('Failed to fetch Stripe balance:', err);
    } finally {
      setLoadingBalance(false);
    }
  };

  const fetchComparison = async () => {
    try {
      setLoadingComparison(true);
      const data = await AdminAPI.get('/api/manage/reconciliation/compare');
      setComparison(data);
    } catch (err) {
      console.error('Failed to fetch comparison:', err);
    } finally {
      setLoadingComparison(false);
    }
  };

  const handleRefresh = () => {
    fetchReconciliation();
    fetchStripeBalance();
    if (comparison) {
      fetchComparison();
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'audit' && !comparison) {
      fetchComparison();
    }
  };

  const getAvailableBalance = () => {
    if (!stripeBalance?.available) return 0;
    const usdBalance = stripeBalance.available.find(b => b.currency === 'usd');
    return usdBalance?.amount || 0;
  };

  const getPendingBalance = () => {
    if (!stripeBalance?.pending) return 0;
    const usdBalance = stripeBalance.pending.find(b => b.currency === 'usd');
    return usdBalance?.amount || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-xl">Loading reconciliation data from Stripe...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white p-8">
        <div className="max-w-7xl mx-auto">
          <Link to="/manage" className="flex items-center space-x-2 text-gray-400 hover:text-white mb-4">
            <FaArrowLeft />
            <span>Back to Dashboard</span>
          </Link>
          <div className="bg-red-500/20 border border-red-500 rounded-lg p-6">
            <h2 className="text-xl font-bold text-red-400 mb-2">Error</h2>
            <p>{error}</p>
            <button
              onClick={handleRefresh}
              className="mt-4 px-4 py-2 bg-red-500 hover:bg-red-600 rounded"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const summary = reconciliation?.summary;
  const creators = reconciliation?.creators || [];

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <Link to="/manage" className="flex items-center space-x-2 text-gray-400 hover:text-white mb-4">
              <FaArrowLeft />
              <span>Back to Dashboard</span>
            </Link>
            <h1 className="text-4xl font-bold">Reconciliation</h1>
            <p className="text-gray-400 mt-2">Financial data from Stripe (source of truth)</p>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition"
          >
            <FaSync className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6 border-b border-gray-800">
          <button
            onClick={() => handleTabChange('overview')}
            className={`pb-3 px-4 ${activeTab === 'overview' ? 'border-b-2 border-blue-500 text-blue-400' : 'text-gray-400 hover:text-white'}`}
          >
            Overview
          </button>
          <button
            onClick={() => handleTabChange('creators')}
            className={`pb-3 px-4 ${activeTab === 'creators' ? 'border-b-2 border-blue-500 text-blue-400' : 'text-gray-400 hover:text-white'}`}
          >
            Creator Balances
          </button>
          <button
            onClick={() => handleTabChange('audit')}
            className={`pb-3 px-4 ${activeTab === 'audit' ? 'border-b-2 border-blue-500 text-blue-400' : 'text-gray-400 hover:text-white'}`}
          >
            DB vs Stripe Audit
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-900 p-6 rounded-lg border border-gray-800">
                <div className="flex items-center space-x-3 mb-3">
                  <FaChartLine className="text-green-400 text-xl" />
                  <span className="text-gray-400">Gross Donations</span>
                </div>
                <p className="text-3xl font-bold text-green-400">
                  {formatCurrency(summary?.total_gross_donations || 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">{summary?.donation_count || 0} donations</p>
              </div>

              <div className="bg-gray-900 p-6 rounded-lg border border-gray-800">
                <div className="flex items-center space-x-3 mb-3">
                  <FaPercentage className="text-purple-400 text-xl" />
                  <span className="text-gray-400">Platform Revenue (5% + $0.50)</span>
                </div>
                <p className="text-3xl font-bold text-purple-400">
                  {formatCurrency(summary?.total_platform_fees || 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Margin after Stripe fees: {formatCurrency(summary?.total_castle_margin || 0)}
                </p>
              </div>

              <div className="bg-gray-900 p-6 rounded-lg border border-gray-800">
                <div className="flex items-center space-x-3 mb-3">
                  <FaExchangeAlt className="text-blue-400 text-xl" />
                  <span className="text-gray-400">Already Transferred</span>
                </div>
                <p className="text-3xl font-bold text-blue-400">
                  {formatCurrency(summary?.total_transferred || 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">Paid to creators</p>
              </div>

              <div className="bg-gray-900 p-6 rounded-lg border border-gray-800">
                <div className="flex items-center space-x-3 mb-3">
                  <FaWallet className="text-yellow-400 text-xl" />
                  <span className="text-gray-400">Total Owed to Creators</span>
                </div>
                <p className="text-3xl font-bold text-yellow-400">
                  {formatCurrency(summary?.total_owed || 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">{summary?.creator_count || 0} creators with balance</p>
              </div>
            </div>

            {/* Stripe Balance */}
            <div className="bg-gray-900 p-6 rounded-lg border border-gray-800">
              <h2 className="text-xl font-semibold mb-4">Stripe Account Balance</h2>
              {loadingBalance ? (
                <p className="text-gray-400">Loading...</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-800 p-4 rounded">
                    <p className="text-gray-400 text-sm">Available for Transfer</p>
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(getAvailableBalance())}</p>
                  </div>
                  <div className="bg-gray-800 p-4 rounded">
                    <p className="text-gray-400 text-sm">Pending (Processing)</p>
                    <p className="text-2xl font-bold text-yellow-400">{formatCurrency(getPendingBalance())}</p>
                  </div>
                </div>
              )}
              {getAvailableBalance() < (summary?.total_owed || 0) && (
                <div className="mt-4 p-3 bg-yellow-500/20 border border-yellow-500 rounded text-yellow-400 text-sm">
                  <FaExclamationTriangle className="inline mr-2" />
                  Available balance is less than total owed. Some payouts may need to wait for pending funds.
                </div>
              )}
            </div>

            {/* Breakdown */}
            <div className="bg-gray-900 p-6 rounded-lg border border-gray-800">
              <h2 className="text-xl font-semibold mb-4">How It's Calculated</h2>
              <div className="space-y-3 font-mono text-sm">
                <div className="flex justify-between py-2 border-b border-gray-800">
                  <span className="text-gray-400">Gross Tips</span>
                  <span className="text-green-400">+ {formatCurrency(summary?.total_gross_donations || 0)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-800">
                  <span className="text-gray-400">Already Transferred</span>
                  <span className="text-red-400">- {formatCurrency(summary?.total_transferred || 0)}</span>
                </div>
                <div className="flex justify-between py-2 text-lg font-bold">
                  <span>Total Owed to Creators</span>
                  <span className="text-yellow-400">= {formatCurrency(summary?.total_owed || 0)}</span>
                </div>
                <p className="text-xs text-gray-600 pt-1">
                  Creators get 100% of every tip. Castle's 5% + $0.50 fee is a surcharge charged to the
                  donor on top of the tip, never a deduction from what's owed above.
                </p>
                <div className="flex justify-between py-2 mt-2 border-t border-gray-800 text-gray-500">
                  <span>Platform fee revenue (charged to the donor, informational)</span>
                  <span>{formatCurrency(summary?.total_platform_fees || 0)}</span>
                </div>
                <div className="flex justify-between py-1 text-gray-500">
                  <span>Stripe processing fees (Castle's cost, informational)</span>
                  <span>{formatCurrency(summary?.total_stripe_fees || 0)}</span>
                </div>
                <div className="flex justify-between py-1 text-gray-500">
                  <span>Castle margin (platform fee − Stripe fees)</span>
                  <span>{formatCurrency(summary?.total_castle_margin || 0)}</span>
                </div>
              </div>
            </div>

            {summary?.reconciled_at && (
              <p className="text-gray-500 text-sm text-center">
                Data fetched from Stripe at: {new Date(summary.reconciled_at).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* Creators Tab */}
        {activeTab === 'creators' && (
          <div className="space-y-6">
            <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="text-left p-4">Creator</th>
                      <th className="text-right p-4">Gross</th>
                      <th className="text-right p-4">Transferred</th>
                      <th className="text-right p-4">Owed</th>
                      <th className="text-right p-4 text-gray-500">Platform Fee*</th>
                      <th className="text-right p-4 text-gray-500">Stripe Fee*</th>
                      <th className="text-center p-4">Donations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creators.filter(c => c.donation_count > 0).map((creator, index) => (
                      <tr key={creator.creator_id} className={`border-t border-gray-800 ${index % 2 === 0 ? 'bg-gray-900' : 'bg-gray-900/50'}`}>
                        <td className="p-4">
                          <div>
                            <p className="font-medium">{creator.creator_email}</p>
                            {creator.stripe_account_id ? (
                              <p className="text-xs text-green-400">
                                <FaCheckCircle className="inline mr-1" />
                                Stripe Connected
                              </p>
                            ) : (
                              <p className="text-xs text-yellow-400">
                                <FaExclamationTriangle className="inline mr-1" />
                                No Stripe Account
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-right text-green-400">{formatCurrency(creator.gross_donations)}</td>
                        <td className="p-4 text-right text-blue-400">-{formatCurrency(creator.total_transferred)}</td>
                        <td className="p-4 text-right font-bold">
                          <span className={creator.net_available >= 0 ? 'text-yellow-400' : 'text-red-400'}>
                            {formatCurrency(creator.net_available)}
                          </span>
                        </td>
                        <td className="p-4 text-right text-gray-500">{formatCurrency(creator.platform_fee)}</td>
                        <td className="p-4 text-right text-gray-500">{formatCurrency(creator.stripe_fees)}</td>
                        <td className="p-4 text-center text-gray-400">{creator.donation_count}</td>
                      </tr>
                    ))}
                    {creators.filter(c => c.donation_count > 0).length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-gray-400">
                          No creators with donations found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 p-4">
                * Platform fee is charged to the donor on top of the tip; Stripe fee is Castle's processing cost. Neither is deducted from what the creator is owed.
              </p>
            </div>
          </div>
        )}

        {/* Audit Tab */}
        {activeTab === 'audit' && (
          <div className="space-y-6">
            {loadingComparison ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-xl">Comparing database with Stripe...</div>
              </div>
            ) : comparison ? (
              <>
                {/* Sync Status */}
                <div className={`p-6 rounded-lg border ${comparison.summary.in_sync ? 'bg-green-500/10 border-green-500' : 'bg-yellow-500/10 border-yellow-500'}`}>
                  <div className="flex items-center space-x-3">
                    {comparison.summary.in_sync ? (
                      <>
                        <FaCheckCircle className="text-green-400 text-2xl" />
                        <div>
                          <h3 className="text-xl font-bold text-green-400">In Sync</h3>
                          <p className="text-gray-400">Database records match Stripe data</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <FaExclamationTriangle className="text-yellow-400 text-2xl" />
                        <div>
                          <h3 className="text-xl font-bold text-yellow-400">Discrepancies Found</h3>
                          <p className="text-gray-400">Some records don't match between DB and Stripe</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Comparison Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-900 p-6 rounded-lg border border-gray-800">
                    <h3 className="text-lg font-semibold mb-4">Database Records</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Completed Donations</span>
                        <span>{comparison.summary.db_donation_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Total Amount</span>
                        <span>{formatCurrency(comparison.summary.db_total_amount)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-900 p-6 rounded-lg border border-gray-800">
                    <h3 className="text-lg font-semibold mb-4">Stripe Records</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Successful Payments</span>
                        <span>{comparison.summary.stripe_payment_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Total Amount</span>
                        <span>{formatCurrency(comparison.summary.stripe_total_amount)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Discrepancies */}
                {!comparison.summary.in_sync && (
                  <div className="space-y-4">
                    {comparison.discrepancies.in_stripe_not_db_count > 0 && (
                      <div className="bg-gray-900 p-6 rounded-lg border border-yellow-500">
                        <h3 className="text-lg font-semibold mb-4 text-yellow-400">
                          In Stripe but not in DB ({comparison.discrepancies.in_stripe_not_db_count})
                        </h3>
                        <p className="text-gray-400 text-sm mb-4">
                          These payments succeeded in Stripe but weren't recorded in your database. This usually means a webhook failed.
                        </p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-800">
                              <tr>
                                <th className="text-left p-2">Payment ID</th>
                                <th className="text-right p-2">Amount</th>
                                <th className="text-right p-2">Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {comparison.discrepancies.in_stripe_not_db.slice(0, 10).map((item, i) => (
                                <tr key={i} className="border-t border-gray-800">
                                  <td className="p-2 font-mono text-xs">{item.payment_id}</td>
                                  <td className="p-2 text-right">{formatCurrency(item.stripe_amount)}</td>
                                  <td className="p-2 text-right text-gray-400">
                                    {new Date(item.created * 1000).toLocaleDateString()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {comparison.discrepancies.in_db_not_stripe_count > 0 && (
                      <div className="bg-gray-900 p-6 rounded-lg border border-orange-500">
                        <h3 className="text-lg font-semibold mb-4 text-orange-400">
                          In DB but not in Stripe ({comparison.discrepancies.in_db_not_stripe_count})
                        </h3>
                        <p className="text-gray-400 text-sm mb-4">
                          These records exist in your database but weren't found in Stripe. Could be test data or deleted payments.
                        </p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-800">
                              <tr>
                                <th className="text-left p-2">Payment ID</th>
                                <th className="text-left p-2">Donor</th>
                                <th className="text-right p-2">Amount</th>
                                <th className="text-right p-2">Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {comparison.discrepancies.in_db_not_stripe.slice(0, 10).map((item, i) => (
                                <tr key={i} className="border-t border-gray-800">
                                  <td className="p-2 font-mono text-xs">{item.payment_id}</td>
                                  <td className="p-2">{item.donor || 'Anonymous'}</td>
                                  <td className="p-2 text-right">{formatCurrency(item.db_amount)}</td>
                                  <td className="p-2 text-right text-gray-400">
                                    {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {comparison.discrepancies.amount_mismatches_count > 0 && (
                      <div className="bg-gray-900 p-6 rounded-lg border border-red-500">
                        <h3 className="text-lg font-semibold mb-4 text-red-400">
                          Amount Mismatches ({comparison.discrepancies.amount_mismatches_count})
                        </h3>
                        <p className="text-gray-400 text-sm mb-4">
                          These payments exist in both systems but have different amounts.
                        </p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-800">
                              <tr>
                                <th className="text-left p-2">Payment ID</th>
                                <th className="text-right p-2">DB Amount</th>
                                <th className="text-right p-2">Stripe Amount</th>
                                <th className="text-right p-2">Difference</th>
                              </tr>
                            </thead>
                            <tbody>
                              {comparison.discrepancies.amount_mismatches.map((item, i) => (
                                <tr key={i} className="border-t border-gray-800">
                                  <td className="p-2 font-mono text-xs">{item.payment_id}</td>
                                  <td className="p-2 text-right">{formatCurrency(item.db_amount)}</td>
                                  <td className="p-2 text-right">{formatCurrency(item.stripe_amount)}</td>
                                  <td className="p-2 text-right text-red-400">{formatCurrency(item.difference)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <button
                  onClick={fetchComparison}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg"
                >
                  Run Comparison
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReconciliation;